import React, { useState } from "react";
import { Box, IconButton } from "@mui/material";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import useTheme from "../../../../common/hooks/useTheme";
import { Theme } from "../../../../providers/ThemeProvider";
import NumericInputField from "./NumericInputField";

/**
 * `NumericInputField` with the up/down scale arrows the profile's Onboarding
 * fields have.
 *
 * A sibling variant rather than a prop on `NumericInputField`, so the callers
 * that want a plain numeric box — the Logic Builder, the service builder —
 * are untouched. Pick this one when a value has a natural step and bounds
 * (age, weight, height); pick `NumericInputField` for a free number.
 *
 * The Onboarding stepper itself could not be shared: it lives inside
 * `biometricTab/CustomInputField`, which reads its value from
 * `answers.biometricQuest.myAnswers[category]` and dispatches every change to
 * Redux, so it only works for a biometric field of the logged-in subject.
 * This one is controlled by its props, so it works for any value — including
 * a local draft. Its metrics are copied from that field so the two look
 * identical: 21px chevrons in a 21px button, stacked at the right edge,
 * primary-coloured while the input has focus and grey otherwise, each disabled
 * at its bound.
 *
 * Bounds apply to the arrows only. Typing is left alone — clamping mid-entry
 * fights the user, since typing "1" on the way to "180" would jump to the
 * minimum — so a caller that must not store an out-of-range value checks it
 * on save.
 */
interface NumericInputFieldScaleProps {
  label: string;
  /** Null renders an empty field; the arrows then start from `min`. */
  value: number | null;
  onChange: (value: number | null) => void;
  unit?: string;
  min: number;
  max: number;
  /** Arrow increment. */
  step?: number;
  /** Decimals to keep — 1 for kg, 0 for whole units like years or cm. */
  decimals?: number;
  name: string;
  /** Also accept a typed "," as the decimal key; shown and emitted as ".". */
  acceptCommaDecimal?: boolean;
}

const NumericInputFieldScale: React.FC<NumericInputFieldScaleProps> = ({
  label,
  value,
  onChange,
  unit,
  min,
  max,
  step = 1,
  decimals = 0,
  name,
  acceptCommaDecimal = false,
}) => {
  const theme: Theme = useTheme();
  const [focused, setFocused] = useState(false);

  const round = (n: number) => Number(n.toFixed(decimals));
  const clamp = (n: number) => round(Math.min(max, Math.max(min, n)));

  // An empty field steps to `min` rather than to `step` — the first press
  // should land on a legal value, not on 1 kg.
  const nudge = (direction: 1 | -1) =>
    onChange(value === null ? min : clamp(value + direction * step));

  return (
    <Box
      sx={styles.wrapper}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
    >
      <NumericInputField
        label={label}
        unit={unit}
        value={value === null ? "" : String(value)}
        allowNegative={false}
        acceptCommaDecimal={acceptCommaDecimal}
        // Typing is limited to `decimals` up front, so a whole-unit field
        // (feet, years, cm) refuses the decimal point instead of silently
        // rounding "5.5" to 6 after the fact.
        decimalScale={decimals}
        name={name}
        handleChange={(event) => {
          const raw = event.target.value;
          if (raw === "") return onChange(null);
          const parsed = Number(raw);
          onChange(Number.isNaN(parsed) ? null : round(parsed));
        }}
      />

      <Box sx={styles.arrows}>
        <IconButton
          aria-label={`increase ${label.toLowerCase()}`}
          onClick={() => nudge(1)}
          disabled={value !== null && value >= max}
          sx={styles.arrowButton(theme, focused)}
        >
          <KeyboardArrowUpIcon sx={styles.arrowIcon} />
        </IconButton>
        <IconButton
          aria-label={`decrease ${label.toLowerCase()}`}
          onClick={() => nudge(-1)}
          disabled={value !== null && value <= min}
          sx={styles.arrowButton(theme, focused)}
        >
          <KeyboardArrowDownIcon sx={styles.arrowIcon} />
        </IconButton>
      </Box>
    </Box>
  );
};

export default NumericInputFieldScale;

const styles = {
  wrapper: {
    position: "relative",
    width: "100%",
  },
  arrows: {
    display: "flex",
    flexDirection: "column",
    position: "absolute",
    top: "50%",
    right: "4px",
    transform: "translateY(-50%)",
    zIndex: 50,
  },
  arrowButton: (theme: Theme, focused: boolean) => ({
    padding: "0",
    width: "21px",
    height: "21px",
    color: focused
      ? theme.colors_ds.PRIMARY_ICON_MAIN
      : theme.colors_ds.NEUTRAL_TEXT_WEAK,
  }),
  arrowIcon: {
    fontSize: "21px",
    color: "inherit",
  },
};
